import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, type Nota } from '../api';
import { Cabecera, Cargando, AvisoError, Confirmacion, Vacio } from '../componentes';
import { useCola } from '../hooks';
import { conCache, olvidarDelCache } from '../offline/cache';
import { encolarBorradoDeNota, encolarNota } from '../offline/cola';
import { TarjetaNota, type NotaPintable } from './TarjetaNota';

export function Notas({
  alAbrirNota,
  alVerHistorico,
  alSalir,
}: {
  alAbrirNota: (ref: string | number, nombre: string) => void;
  alVerHistorico: () => void;
  alSalir: () => void;
}) {
  const [delServidor, setDelServidor] = useState<Nota[] | null>(null);
  const [desdeCache, setDesdeCache] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [creando, setCreando] = useState(false);
  const [nombre, setNombre] = useState('');
  const [borrando, setBorrando] = useState<NotaPintable | null>(null);
  const cola = useCola();

  async function borrarNota(nota: NotaPintable) {
    setBorrando(null);
    setError(null);
    try {
      await encolarBorradoDeNota(nota.ref);
      if (typeof nota.ref === 'number') await olvidarDelCache(`nota:${nota.ref}`);
      await olvidarDelCache('notas');
      // Quitarla ya de la lista: el borrado puede tardar si no hay cobertura, y
      // verla seguir ahí después de confirmar parece que no ha funcionado.
      setDelServidor((notas) => (notas ?? []).filter((n) => n.id !== nota.id));
    } catch (fallo) {
      setError(fallo);
    }
  }

  const cargar = useCallback(async () => {
    setError(null);
    try {
      const resultado = await conCache('notas', () => api.notas());
      setDelServidor(resultado.datos);
      setDesdeCache(resultado.desdeCache);
    } catch (fallo) {
      setError(fallo);
      setDelServidor([]);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar, cola.pendientes]);

  async function crear(evento: FormEvent) {
    evento.preventDefault();
    const texto = nombre.trim();
    if (!texto) return;

    // Se encola siempre, haya red o no: así el botón responde igual de rápido
    // en el recibidor de un cliente que en la oficina.
    const ref = await encolarNota(texto);
    setNombre('');
    setCreando(false);
    alAbrirNota(ref, texto);
  }

  const locales = cola.locales.filter((l) => l.tipo === 'nota');

  const notas: NotaPintable[] = [
    ...locales.map((local) => ({
      id: -1,
      ref: local.idLocal,
      name: local.datos.name as string,
      state: 'draft',
      totalAmount: 0,
      paymentMode: null,
      expenseIds: [],
      // Una nota que aún no ha subido no tiene proveedor ni responsable: esos
      // los pone Gextia al crearla y solo se pueden elegir después.
      vendorId: null,
      vendorName: '',
      vendorRequired: false,
      approverId: null,
      approverName: '',
      pendiente: true,
    })),
    ...(delServidor ?? []).map((nota) => ({
      ...nota,
      ref: nota.id as string | number,
      pendiente: false,
    })),
  ];

  return (
    <>
      <Cabecera
        titulo="Mis notas"
        accion={
          <button type="button" className="boton icono" onClick={alSalir} aria-label="Salir">
            ⎋
          </button>
        }
      />

      {desdeCache && (
        <div className="barra-estado">
          Sin conexión: esto es lo último que vimos. Puedes seguir añadiendo gastos.
        </div>
      )}

      <div className="contenido">
        {!desdeCache && <AvisoError error={error} />}

        {delServidor === null ? (
          <Cargando />
        ) : notas.length === 0 ? (
          <Vacio
            titulo="No tienes notas abiertas."
            sugerencia="Crea una al empezar la visita y ve añadiendo los tickets."
          />
        ) : (
          <ul className="lista">
            {notas.map((nota) => (
              <TarjetaNota
                key={String(nota.ref)}
                nota={nota}
                alAbrir={() => alAbrirNota(nota.ref, nota.name)}
                alBorrar={() => setBorrando(nota)}
              />
            ))}
          </ul>
        )}

      </div>

      {borrando && (
        <Confirmacion
          titulo={`¿Borrar «${borrando.name}»?`}
          peligrosa
          textoConfirmar="Borrar"
          detalle={
            <>
              <p>
                {borrando.pendiente
                  ? 'Todavía no ha subido a Gextia, así que solo se quita de aquí.'
                  : 'Se borrará con todos sus gastos y fotos, también en Gextia.'}
              </p>
              {!borrando.pendiente && (
                <p className="tarjeta-meta">No se puede deshacer.</p>
              )}
            </>
          }
          alConfirmar={() => void borrarNota(borrando)}
          alCancelar={() => setBorrando(null)}
        />
      )}

      <div className="acciones-abajo">
        {creando ? (
          <form
            onSubmit={(e) => void crear(e)}
            style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}
          >
            <div className="campo">
              <label htmlFor="nombre-nota">Nombre de la nota</label>
              <input
                id="nombre-nota"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Visita a Arte Miranda"
                autoFocus
                required
              />
            </div>
            <button type="submit" className="boton ancho" disabled={!nombre.trim()}>
              Crear nota
            </button>
            <button
              type="button"
              className="boton secundario ancho"
              onClick={() => setCreando(false)}
            >
              Cancelar
            </button>
          </form>
        ) : (
          <>
            <button type="button" className="boton ancho" onClick={() => setCreando(true)}>
              Nueva nota
            </button>
            {/* Discreto y debajo: es consulta, no trabajo. Lo que se viene a
                hacer aquí es crear una nota, y eso se queda con el botón
                sólido. */}
            <button type="button" className="boton secundario ancho" onClick={alVerHistorico}>
              <span aria-hidden="true">🗂</span> Ver histórico
            </button>
          </>
        )}
      </div>
    </>
  );
}
